import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Popover,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  AmlScenario,
  CATEGORY_LABELS,
  ScenarioCategory,
  ScenarioParam,
  getRiskColor,
  getTriggerActionLabel,
} from '../../mocks/amlScenariosMocks';
import ApiService from '../../helpers/apiService';

const emptyScenario = (): Omit<AmlScenario, 'id' | 'createdAt' | 'updatedAt'> => ({
  code: '',
  name: '',
  description: '',
  category: 'aml_check',
  riskCoefficient: 50,
  params: [],
  triggerAction: 'notify',
  isActive: true,
  regulatoryBasis: '',
});

/** Map backend scenario to frontend AmlScenario shape */
function backendToScenario(b: any): AmlScenario {
  let params: ScenarioParam[] = [];
  if (Array.isArray(b.params)) {
    params = b.params;
  } else if (b.threshold != null) {
    params = [{ key: 'threshold', label: 'Порог', value: b.threshold, unit: b.currency ?? undefined }];
  }
  return {
    id: b.id,
    code: b.code ?? '',
    name: b.name ?? '',
    description: b.description ?? '',
    category: (b.category as ScenarioCategory) ?? 'aml_check',
    riskCoefficient: b.risk_coefficient ?? 50,
    params,
    triggerAction: (b.trigger_action as AmlScenario['triggerAction']) ?? 'notify',
    isActive: b.is_active ?? true,
    regulatoryBasis: b.regulatory_basis ?? '',
    createdAt: b.created_at ?? new Date().toISOString(),
    updatedAt: b.updated_at ?? new Date().toISOString(),
  };
}

const AmlScenariosTab: React.FC = () => {
  const [scenarios, setScenarios] = useState<AmlScenario[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<AmlScenario | null>(null);
  const [form, setForm] = useState<Omit<AmlScenario, 'id' | 'createdAt' | 'updatedAt'>>(emptyScenario());
  const [paramsText, setParamsText] = useState('');

  // Load scenarios from backend on mount
  useEffect(() => {
    setLoading(true);
    ApiService.amlGetScenarios()
      .then((data) => setScenarios(data.map(backendToScenario)))
      .catch((err) => console.error('amlGetScenarios error:', err))
      .finally(() => setLoading(false));
  }, []);

  const openAdd = () => {
    setEditTarget(null);
    const blank = emptyScenario();
    setForm(blank);
    setParamsText('');
    setShowModal(true);
  };

  const openEdit = (s: AmlScenario) => {
    setEditTarget(s);
    setForm({
      code: s.code,
      name: s.name,
      description: s.description,
      category: s.category,
      riskCoefficient: s.riskCoefficient,
      params: s.params,
      triggerAction: s.triggerAction,
      isActive: s.isActive,
      regulatoryBasis: s.regulatoryBasis,
    });
    setParamsText(s.params.map((p) => `${p.key}=${p.value}${p.unit ? ' ' + p.unit : ''}`).join('\n'));
    setShowModal(true);
  };

  const parseParams = (): ScenarioParam[] =>
    paramsText
      .split('\n')
      .filter((l) => l.trim())
      .map((line) => {
        const eq = line.indexOf('=');
        if (eq === -1) return { key: line.trim(), label: line.trim(), value: '' };
        const key = line.slice(0, eq).trim();
        const rest = line.slice(eq + 1).trim();
        const spaceIdx = rest.search(/\s+/);
        const value = spaceIdx === -1 ? rest : rest.slice(0, spaceIdx);
        const unit = spaceIdx === -1 ? undefined : rest.slice(spaceIdx + 1).trim();
        const numVal = Number(value);
        return { key, label: key, value: isNaN(numVal) ? value : numVal, unit };
      });

  const handleSave = async () => {
    const parsedParams = parseParams();

    // Extract threshold from params if present
    const thresholdParam = parsedParams.find((p) => p.key === 'threshold');
    const threshold = thresholdParam ? Number(thresholdParam.value) : undefined;
    const currency = thresholdParam?.unit ?? undefined;

    const payload = {
      code: form.code,
      name: form.name,
      description: form.description,
      category: form.category,
      severity: form.riskCoefficient >= 80 ? 'high' : form.riskCoefficient >= 50 ? 'medium' : 'low',
      risk_coefficient: form.riskCoefficient,
      params: parsedParams,
      trigger_action: form.triggerAction,
      regulatory_basis: form.regulatoryBasis,
      threshold,
      currency,
      is_active: form.isActive,
    };

    try {
      if (editTarget) {
        const updated = await ApiService.amlUpdateScenario(editTarget.id, payload);
        setScenarios((prev) =>
          prev.map((s) =>
            s.id === editTarget.id
              ? { ...backendToScenario(updated), params: parsedParams, riskCoefficient: form.riskCoefficient, triggerAction: form.triggerAction, regulatoryBasis: form.regulatoryBasis }
              : s
          )
        );
      } else {
        const created = await ApiService.amlCreateScenario(payload);
        setScenarios((prev) => [
          ...prev,
          {
            ...backendToScenario(created),
            params: parsedParams,
            riskCoefficient: form.riskCoefficient,
            triggerAction: form.triggerAction,
            regulatoryBasis: form.regulatoryBasis,
          },
        ]);
      }
    } catch (err) {
      console.error('amlSaveScenario error:', err);
    }
    setShowModal(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Удалить сценарий?')) return;
    try {
      await ApiService.amlDeleteScenario(id);
      setScenarios((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error('amlDeleteScenario error:', err);
    }
  };

  const handleToggle = (id: string) => {
    setScenarios((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, isActive: !s.isActive, updatedAt: new Date().toISOString() } : s
      )
    );
  };

  const paramsPopover = (params: ScenarioParam[]) => (
    <Popover style={{ maxWidth: 360 }}>
      <Popover.Header>Параметры сценария</Popover.Header>
      <Popover.Body>
        <table className="table table-sm table-borderless mb-0">
          <tbody>
            {params.map((p) => (
              <tr key={p.key}>
                <td className="text-muted pe-2" style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{p.label}</td>
                <td style={{ fontSize: '0.8rem' }}>
                  <strong>{p.value}</strong>{p.unit ? <span className="text-muted ms-1">{p.unit}</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted" style={{ fontSize: '0.875rem' }}>
          {loading ? (
            <Spinner animation="border" size="sm" className="me-2" />
          ) : (
            <>
              Всего сценариев: <strong>{scenarios.length}</strong>
              {' · '}Активных: <strong className="text-success">{scenarios.filter((s) => s.isActive).length}</strong>
              {' · '}Неактивных: <strong className="text-secondary">{scenarios.filter((s) => !s.isActive).length}</strong>
            </>
          )}
        </div>
        <Button variant="primary" size="sm" onClick={openAdd}>
          + Добавить сценарий
        </Button>
      </div>

      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th style={{ width: 90 }}>Код</th>
            <th>Название</th>
            <th style={{ width: 140 }}>Категория</th>
            <th style={{ width: 110 }}>Коэф. риска</th>
            <th style={{ width: 140 }}>Действие</th>
            <th style={{ width: 80 }}>Параметры</th>
            <th style={{ width: 80 }}>Статус</th>
            <th style={{ width: 110 }}>Действия</th>
          </tr>
        </thead>
        <tbody>
          {scenarios.map((s) => (
            <tr key={s.id} className={s.isActive ? '' : 'text-muted'}>
              <td>
                <code style={{ fontSize: '0.75rem' }}>{s.code}</code>
              </td>
              <td>
                <div style={{ maxWidth: 280 }}>
                  <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{s.name}</div>
                  <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }} title={s.description}>
                    {s.description}
                  </div>
                </div>
              </td>
              <td>
                <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                  {CATEGORY_LABELS[s.category]}
                </Badge>
              </td>
              <td>
                <div className="d-flex align-items-center gap-1">
                  <Badge bg={getRiskColor(s.riskCoefficient)} style={{ fontSize: '0.75rem', minWidth: 36 }}>
                    {s.riskCoefficient}
                  </Badge>
                  <div
                    style={{
                      flex: 1,
                      height: 6,
                      background: '#e9ecef',
                      borderRadius: 3,
                      overflow: 'hidden',
                      minWidth: 40,
                    }}
                  >
                    <div
                      style={{
                        width: `${s.riskCoefficient}%`,
                        height: '100%',
                        background:
                          s.riskCoefficient >= 80
                            ? '#dc3545'
                            : s.riskCoefficient >= 60
                            ? '#ffc107'
                            : '#0dcaf0',
                        borderRadius: 3,
                      }}
                    />
                  </div>
                </div>
              </td>
              <td>
                <Badge
                  bg={
                    s.triggerAction === 'block_and_notify'
                      ? 'danger'
                      : s.triggerAction === 'block'
                      ? 'warning'
                      : s.triggerAction === 'review'
                      ? 'info'
                      : 'secondary'
                  }
                  text={s.triggerAction === 'block' ? 'dark' : undefined}
                  style={{ fontSize: '0.7rem' }}
                >
                  {getTriggerActionLabel(s.triggerAction)}
                </Badge>
              </td>
              <td className="text-center">
                {s.params.length > 0 ? (
                  <OverlayTrigger trigger={['click', 'focus']} placement="left" overlay={paramsPopover(s.params)} rootClose>
                    <Button variant="outline-secondary" size="sm" style={{ fontSize: '0.7rem', padding: '1px 6px' }}>
                      {s.params.length} пар.
                    </Button>
                  </OverlayTrigger>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td>
                <Form.Check
                  type="switch"
                  checked={s.isActive}
                  onChange={() => handleToggle(s.id)}
                  label={s.isActive ? <span className="text-success" style={{ fontSize: '0.75rem' }}>Вкл</span> : <span className="text-secondary" style={{ fontSize: '0.75rem' }}>Выкл</span>}
                />
              </td>
              <td>
                <div className="d-flex gap-1">
                  <Button variant="outline-primary" size="sm" style={{ fontSize: '0.7rem', padding: '2px 6px' }} onClick={() => openEdit(s)}>
                    Ред.
                  </Button>
                  <Button variant="outline-danger" size="sm" style={{ fontSize: '0.7rem', padding: '2px 6px' }} onClick={() => handleDelete(s.id)}>
                    Del
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editTarget ? 'Редактировать сценарий' : 'Добавить сценарий'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-4">
              <Form.Group>
                <Form.Label>Код <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  size="sm"
                  value={form.code}
                  placeholder="SCN-XXX"
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-8">
              <Form.Group>
                <Form.Label>Название <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  size="sm"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Group>
                <Form.Label>Описание</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  size="sm"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-4">
              <Form.Group>
                <Form.Label>Категория</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as ScenarioCategory })}
                >
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-4">
              <Form.Group>
                <Form.Label>Коэффициент риска (0–100)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  size="sm"
                  value={form.riskCoefficient}
                  onChange={(e) => setForm({ ...form, riskCoefficient: Math.min(100, Math.max(0, Number(e.target.value))) })}
                />
                <div className="mt-1">
                  <Badge bg={getRiskColor(form.riskCoefficient)}>Риск: {form.riskCoefficient}</Badge>
                </div>
              </Form.Group>
            </div>
            <div className="col-4">
              <Form.Group>
                <Form.Label>Действие при срабатывании</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.triggerAction}
                  onChange={(e) => setForm({ ...form, triggerAction: e.target.value as AmlScenario['triggerAction'] })}
                >
                  <option value="notify">Уведомление</option>
                  <option value="review">Ручная проверка</option>
                  <option value="block">Блокировка</option>
                  <option value="block_and_notify">Блокировка + уведомление</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Group>
                <Form.Label>
                  Параметры{' '}
                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                    (каждый с новой строки в формате: ключ=значение [единица])
                  </span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  size="sm"
                  placeholder={'threshold_amount=600000 RUB\nperiod_days=30 дней\ncheck_pep=enabled'}
                  value={paramsText}
                  onChange={(e) => setParamsText(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Group>
                <Form.Label>Нормативное основание</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.regulatoryBasis}
                  placeholder="AMLD6 (EU 2018/1673), FATF R.10, ..."
                  onChange={(e) => setForm({ ...form, regulatoryBasis: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Check
                type="switch"
                label="Сценарий активен"
                checked={form.isActive}
                onChange={() => setForm({ ...form, isActive: !form.isActive })}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!form.code.trim() || !form.name.trim()}
            onClick={handleSave}
          >
            {editTarget ? 'Сохранить' : 'Создать'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AmlScenariosTab;
